import {
  CatalogueImportModel,
  RawCatalogueRow,
  RowValidationCatalogueError,
  SystemReferenceData,
} from 'src/pages/Catalog/Components/CatalogueImportDialogue/CustomHooks/types';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';
import { trimOrEmpty } from 'src/utils/CommonHelpers/sharedCSVUtils';

const WARRANTY_NUMERIC_PATTERN = /^\d+(\.\d+)?$/;

const isNonEmptyRow = (row: RawCatalogueRow): boolean => {
  return !!(row.Name && row.Name.trim() !== '');
};

const mapRowToCatalogueModel = (row: RawCatalogueRow): CatalogueImportModel => {
  let properties: { Name: string }[] = [];

  if (row.Properties && row.Properties.trim()) {
    properties = row.Properties.split(/[&,]/)
      .map((p: string) => p.trim())
      .filter((p: string) => p !== '')
      .map((name: string) => ({ Name: name }));
  }

  return {
    Name: trimOrEmpty(row.Name),
    Type: trimOrEmpty(row.Type),
    Warranty: trimOrEmpty(row.Warranty),
    Make: trimOrEmpty(row.Make),
    Model: trimOrEmpty(row.Model),
    Properties: properties,
  };
};

export const buildPropertyNameSet = (
  properties: IProperty[]
): Set<string> => {
  return new Set(
    properties.map((property: IProperty) => property.name.trim().toLowerCase())
  );
};

export const buildTypeNamesFromItemTypes = (
  itemTypes: { id: string; name: string }[]
): Set<string> => {
  return new Set(
    itemTypes.map((t) => t.name.trim().toLowerCase())
  );
};

export const validateCatalogueRows = (
  records: CatalogueImportModel[],
  referenceData: SystemReferenceData
): RowValidationCatalogueError[] => {
  const { typeNames, propertyNames } = referenceData;

  const errors: RowValidationCatalogueError[] = [];

  records.forEach((record: CatalogueImportModel, index: number) => {
    const issues: string[] = [];

    if (!record.Name) {
      issues.push('Name is required.');
    }

    if (record.Properties.length === 0) {
      issues.push('Properties cannot be blank.');
    } else {
      for (const prop of record.Properties) {
        if (!propertyNames.has(prop.Name.trim().toLowerCase())) {
          issues.push(`Property "${prop.Name}" doesn't exist in the system.`);
        }
      }
    }

    if (!record.Type) {
      issues.push('Type is required.');
    } else if (!typeNames.has(record.Type.trim().toLowerCase())) {
      issues.push(`Type "${record.Type}" doesn't exist in the system.`);
    }

    if (!record.Warranty) {
      issues.push('Warranty is required.');
    } else if (!WARRANTY_NUMERIC_PATTERN.test(record.Warranty)) {
      issues.push(
        `Warranty must be a number of years only (e.g. 1). Got: "${record.Warranty}".`
      );
    }

    if (issues.length > 0) {
      errors.push({
        row: index + 1,
        name: record.Name || '(empty)',
        issues,
      });
    }
  });

  return errors;
};

export { isNonEmptyRow, mapRowToCatalogueModel };
